import { ConversationDetails } from "@virtualprogaming/js-public-sdk";
import classNames from "classnames";
import { noop, partial } from "lodash";
import moment from "moment";
import React, { FC, useCallback, useMemo, useState } from "react";

import { useChatConversationMarkRead } from "~/queries";
import { Icon } from "~/ui/components/icons";
import {
  SelectedWindows,
  useChatAuthAccessToken,
  useChatLeftMenuSettings,
  useChatMessageToEdit,
  useChatSelectedWindow,
  useChatToRename,
  useSelectedChat,
} from "~/ui/context";
import { convertEmojisToIcons, useScreenMd } from "~/utils";

import { ConversationActions } from "./ConversationActions";
import { ConversationRename } from "./ConversationRename";

export const Conversation: FC<{
  conversation: ConversationDetails;
  isGlobal?: boolean;
}> = ({ conversation, isGlobal = false }) => {
  const accessToken = useChatAuthAccessToken();
  const [conversationHovered, setConversationHovered] = useState<number | null>(
    null
  );

  const {
    mutateAsync: readConversation,
    isLoading: isReadingChat,
  } = useChatConversationMarkRead();

  const { selectedChat, setSelectedChat } = useSelectedChat();
  const { setSelectedWindow } = useChatSelectedWindow();
  const { chatToRename, setChatToRename } = useChatToRename();
  const { setMessageToEdit } = useChatMessageToEdit();

  const { setIsMenuOpened } = useChatLeftMenuSettings();
  const isDesktop = useScreenMd();
  const handleChatClick = (chatId: number | undefined) => {
    if (!chatId || chatToRename === chatId) return;
    setSelectedChat(chatId);
    setChatToRename(null);
    setSelectedWindow(SelectedWindows.Conversation);
    setMessageToEdit(null);
    if (!isDesktop) {
      setIsMenuOpened(false);
    }
  };
  const handleReadConversation = useCallback(
    (id: number | undefined, last_message_id: number | undefined) => {
      if (!id) return;
      if (last_message_id) {
        readConversation([id, accessToken, last_message_id]);
      }
    },
    [accessToken, readConversation]
  );

  const globalTag = useMemo(
    () => conversation?.tags?.includes("global") ?? false,
    [conversation]
  );

  const convertedMessage = convertEmojisToIcons(
    conversation.last_message?.text ?? ""
  );
  return (
    <div
      onClick={partial(handleChatClick, conversation?.id)}
      key={conversation.id}
      className={classNames(
        "flex flex-col w-100 borderR-05 mg-t-sm justify-between transition-1s",
        selectedChat === conversation.id && "bg-gold text-black",
        conversationHovered === conversation.id &&
          selectedChat !== conversation.id &&
          "bg-light"
      )}
      onMouseOver={partial(setConversationHovered, conversation?.id ?? null)}
      onMouseLeave={partial(setConversationHovered, null)}
    >
      <div
        className="flex flex-col pd-b-xs pointer pd-l-md"
        onClick={
          conversation?.is_unread
            ? partial(
                handleReadConversation,
                conversation?.id,
                conversation.last_message?.id
              )
            : noop
        }
      >
        <div className="flex gap-md items-center">
          <div className="flex gap-md items-center w-90">
            <img
              alt="user"
              src={conversation.avatar_url}
              height={30}
              width={30}
              className="borderR-05"
            />
            <p
              className={classNames(
                selectedChat === conversation.id && "text-bold"
              )}
            >
              {conversation?.display_name &&
              conversation?.display_name?.length > 40
                ? `${conversation.display_name?.substring(0, 40)}...`
                : conversation.display_name}
            </p>
          </div>
          <div className="flex items-center gap-sm w-20">
            {conversation?.is_pinned && (
              <Icon icon={["fas", "thumbtack"]} size="xs" />
            )}
            {conversation?.is_unread && (
              <Icon icon={["fas", "circle"]} size="xs" className="pd-r-md" />
            )}
            {conversation?.tags?.includes("global") && (
              <Icon icon={["far", "globe"]} size="xs" className="pd-r-md" />
            )}
          </div>
        </div>
        <span className="text-muted overline-md">
          {moment(conversation?.last_message?.created_at).fromNow()}
          {!convertedMessage
            ? null
            : convertedMessage !== "" && convertedMessage?.length > 25
            ? ` - ${convertedMessage?.substring(0, 25)}...`
            : ` - ${convertedMessage}`}
        </span>
      </div>
      {chatToRename === conversation.id && (
        <ConversationRename
          conversation={conversation}
          isGlobal={isGlobal || globalTag}
        />
      )}
      {!isGlobal &&
        (selectedChat === conversation.id ||
          conversationHovered === conversation.id) &&
        chatToRename !== conversation.id && (
          <ConversationActions
            conversation={conversation}
            onRead={handleReadConversation}
            isReadingChat={isReadingChat}
          />
        )}
    </div>
  );
};
